import { logger } from '@nykaa/logger';
import logErrors from '@nykaa/logger/logErrors';
import { transformRecoProducts } from '@nykaa/reco-widget-shared/store/transformer';
import { isValidArrayLength } from '@nykaa/utils/validations';
import { ApiHelper } from 'shared/api';
import { ROUTES, getUrl } from 'shared/api/urls';
import { queryStringFromParams } from 'shared/helpers/utils/url';
import { contentTypes } from '../../constants/requestHeaders';
import {
  transformTrendingSearches,
  transformSearchSuggestions,
  sortSearchSuggestions,
  transformPopularSuggestions,
  SearchSuggestionFromAPI,
  SearchSuggestionGroup,
} from './transformer';
import { RemoteConfig } from 'store/remoteConfig/types';

const ERROR_IN_SEARCH_SUGGESTIONS_API = 'Error in Search suggestions API';
const ERROR_IN_TRENDING_SEARCH_API = 'Error in Trending search API';
const ERROR_IN_POPULAR_SUGGESTIONS_API = 'Error in Popular suggestions API';
export const getSearchSuggestions = async (
  searchInput: string,
  isPro: boolean,
  isSearchV2enabled: boolean = false
) => {
  const suggestionsRoute = isSearchV2enabled
    ? ROUTES.SEARCH_SUGGESTIONS
    : ROUTES.SEARCH_SUGGESTIONS_V1;
  const url = getUrl(suggestionsRoute);

  try {
    const params = {
      q: searchInput,
      ...(isSearchV2enabled && { autosuggest_algo: 'special_suggestions' }),
    };
    let otherParams = {};
    if (isPro) {
      otherParams.pro = isPro;
    }
    const queryString = queryStringFromParams({ ...params, ...otherParams });
    const {
      data: { suggestions },
    } = await ApiHelper(`${url}${queryString}`, 'get');

    if (!suggestions) {
      return Promise.reject({
        message: '"suggestions" not found in api -> data',
      });
    }

    const isGrouped =
      isValidArrayLength(suggestions) &&
      Boolean(suggestions[0]?.type) &&
      Array.isArray(suggestions[0]?.suggestions);

    if (isGrouped) {
      const groupedSuggestions = suggestions as SearchSuggestionGroup[];
      const allItems = groupedSuggestions.flatMap((group) => group.suggestions);
      const transformed = transformSearchSuggestions(allItems);
      return {
        suggestions: sortSearchSuggestions(transformed),
        searchInput,
      };
    }

    const transFormedResponse = transformSearchSuggestions(
      suggestions as SearchSuggestionFromAPI[]
    );
    return {
      suggestions: sortSearchSuggestions(transFormedResponse),
      searchInput,
    };
  } catch (e) {
    logErrors(e, `${ERROR_IN_SEARCH_SUGGESTIONS_API} :url=${url}`);
    throw e;
  }
};

export const getPopularSuggestions = async (remoteConfig: RemoteConfig) => {
  const formData = new FormData();
  const widget = remoteConfig?.configs?.popularSuggestionWidget?.widget;
  widget && formData.append('widgets', JSON.stringify(widget));
  formData.append('store', __PLATFORM__);

  const url = getUrl(ROUTES.POPULAR_SUGGESTIONS);
  try {
    const response = await ApiHelper(url, 'post', formData, {
      'Content-Type': contentTypes.URL_ENCODED,
    });
    if (!response?.data) {
      return Promise.reject({
        message: '"Popular suggestions" not found in api -> data',
      });
    }
    return transformPopularSuggestions(response.data.result);
  } catch (error) {
    const errorMessage = `${ERROR_IN_POPULAR_SUGGESTIONS_API} :url=${url}`;
    logger.error(error, errorMessage);
    throw error;
  }
};

export const getTrendingSearches = async () => {
  const url = getUrl(ROUTES.TRENDING_SEARCHES);

  try {
    const {
      data: { response },
    } = await ApiHelper(`${url}`, 'get');
    const { trending } = response;
    if (!trending) {
      return Promise.reject({
        message: '"trending" searches not found in api -> data',
      });
    }

    return transformTrendingSearches(trending);
  } catch (e) {
    logErrors(e, `${ERROR_IN_TRENDING_SEARCH_API} :url=${url}`);
    throw e;
  }
};

export const getBulkProducts = async (ids: string) => {
  const apiEndpoint = `${getUrl(
    ROUTES.PRODUCT_BULK
  )}?product_ids=${ids}&store=${__PLATFORM__}`;

  try {
    const {
      data: { response },
    } = await ApiHelper(apiEndpoint, 'get');
    const { products } = response;
    if (!products) {
      return Promise.reject({
        message: 'Recently viewed products not found in response',
      });
    }
    return transformRecoProducts(products);
  } catch (error) {
    logger.error(error, 'Recently viewed products not found in response');
    throw error;
  }
};
